import { ButtonList, DropdownButton } from '@dixa/lib-shared-habla';
import {
  ArticleData,
  ArticleDataContext,
} from 'articleEditor/contexts/articleDataProvider';
import { ArticleSavingContext } from 'articleEditor/contexts/savingContext';
import { TranslationDataContext } from 'articleEditor/contexts/translationProvider';
import { useArticleDisplayTitle } from 'articleEditor/hooks/useArticleDisplayTitle';
import { PublicationState } from 'articleEditor/hooks/useArticlePublicationState';
import { ArticlePublishErrorsModal } from 'articleEditor/modals/articlePublishErrorsModal';
import { ArticlePublishModal } from 'articleEditor/modals/articlePublishModal';
import { ArticleTranslationPublishModal } from 'articleEditor/modals/articleTranslationPublishModal';
import { ArticleTranslationUnpublishModal } from 'articleEditor/modals/articleTranslationUnpublishModal';
import { ArticleUnPublishModal } from 'articleEditor/modals/articleUnpublishModal';
import { DiscardChangesModal } from 'articleEditor/modals/discardChangesModal';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import { PublicationStatus } from 'lib-knowledge-management-common/graphql';
import { useModal } from 'lib-knowledge-modal';
import { useContext } from 'react';

import { articleSettingsActions } from '../settings/settings.store';
import s from './headerControls.module.scss';
import InfoItem from './infoItem';
import TimeAgo from './timeAgo';

export const PublishingDropdown = (): JSX.Element => {
  const { showModal } = useModal();
  const article = useContext(ArticleDataContext);
  const translation = useContext(TranslationDataContext);
  const { actions } = useContext(ArticleSavingContext);
  const { selectedLanguage } = useContext(LanguageContext);
  const getArticleTitle = useArticleDisplayTitle();
  const translationPublicationState = ((): PublicationState => {
    switch (translation.publicationStatus) {
      case PublicationStatus.Published:
        return 'published';
      case PublicationStatus.PublishedWithDraft:
        return 'work_in_progress';
      case PublicationStatus.NeverPublished:
      case PublicationStatus.Unpublished:
      default:
        return 'draft';
    }
  })();

  if (!article) return <></>;

  const publish = async () => {
    // TODO: do more validation later.
    const hasValidCategory =
      !!article.parent && article.parent.__typename === 'Category';

    if (!hasValidCategory) {
      showModal(ArticlePublishErrorsModal, {
        articleTitle: getArticleTitle(),
        categoryNotSet: !hasValidCategory,
        bodiesEmpty: [],
        titlesEmpty: [],
      });
      articleSettingsActions.blurCategoryDropdown();
      return;
    }

    showModal(ArticlePublishModal, {
      articleId: article.id,
      articleTitle: getArticleTitle(),
    });
  };

  const publishTranslation = async () => {
    const hasValidCategory =
      !!article.parent && article.parent.__typename === 'Category';

    if (!hasValidCategory) {
      showModal(ArticlePublishErrorsModal, {
        articleTitle: getArticleTitle(),
        categoryNotSet: !hasValidCategory,
        bodiesEmpty: [],
        titlesEmpty: [],
      });
      articleSettingsActions.blurCategoryDropdown();
      return;
    }

    showModal(ArticleTranslationPublishModal, {
      articleId: article.id,
      articleTitle: getArticleTitle(),
      languageId: selectedLanguage.languageId,
      languageName: selectedLanguage.description,
    });
  };

  const unpublishTranslation = () =>
    showModal(ArticleTranslationUnpublishModal, {
      articleId: article.id,
      articleTitle: getArticleTitle(),
      languageId: selectedLanguage.languageId,
      languageName: selectedLanguage.description,
    });

  const unpublish = () =>
    showModal(ArticleUnPublishModal, {
      articleId: article.id,
      articleTitle: getArticleTitle(),
    });

  const discardChanges = () =>
    showModal(DiscardChangesModal, {
      articleId: article.id,
      articleTitle: translation.draft.title,
      languageId: selectedLanguage.languageId,
      onDiscard(data) {
        actions.resetValueToValue({
          body: data?.articleTranslationRevertDraft?.content?.children || [],
          title: data?.articleTranslationRevertDraft?.title || '',
        });
      },
    });

  const publishButtonVariant = {
    draft: 'primary',
    work_in_progress: 'primary',
    published: 'secondary',
  } as const;

  return (
    <DropdownButton
      buttonProps={{
        variant: publishButtonVariant[translationPublicationState],
      }}
      dropdown={
        <>
          {translationPublicationState === 'draft' && (
            <Draft
              article={article}
              publish={publish}
              publishTranslation={publishTranslation}
            />
          )}
          {translationPublicationState === 'work_in_progress' && (
            <WorkInProgress
              article={article}
              discardChanges={discardChanges}
              publish={publish}
              publishTranslation={publishTranslation}
            />
          )}
          {translationPublicationState === 'published' && (
            <Published
              article={article}
              unpublish={unpublish}
              unpublishTranslation={unpublishTranslation}
            />
          )}
        </>
      }
    >
      {translationPublicationState === 'draft' && 'Publish'}
      {translationPublicationState === 'work_in_progress' && 'Publish Changes'}
      {translationPublicationState === 'published' && 'Published'}
    </DropdownButton>
  );
};

type DraftProps = {
  publish: () => void;
  publishTranslation: () => void;
  article: ArticleData;
};

const Draft = ({ publish, publishTranslation }: DraftProps) => {
  return (
    <ButtonList>
      <ButtonList.Item icon="cloud-upload" onClick={publishTranslation}>
        Publish
      </ButtonList.Item>
      <ButtonList.Item icon="cloud-upload" onClick={publish}>
        Publish All
      </ButtonList.Item>

      <ButtonList.Divider />
      <InfoItem>Never published</InfoItem>
    </ButtonList>
  );
};

type WorkInProgressProps = {
  publish: () => void;
  publishTranslation: () => void;
  discardChanges: () => void;
  article: ArticleData;
};

const WorkInProgress = ({
  publish,
  publishTranslation,
  discardChanges,
  article,
}: WorkInProgressProps) => {
  return (
    <ButtonList>
      <ButtonList.Item icon="cloud-upload" onClick={publishTranslation}>
        Publish
      </ButtonList.Item>
      <ButtonList.Item icon="cloud-upload" onClick={publish}>
        Publish All
      </ButtonList.Item>
      <ButtonList.Divider />

      <ButtonList.Item
        buttonClassName={s.buttonWarning}
        className={s.buttonWarning}
        icon="pen-slash"
        onClick={discardChanges}
      >
        Discard changes
      </ButtonList.Item>

      <ButtonList.Divider />
      {article.indexedAt && article.indexedBy && (
        <TimeAgo
          labelPrefix="Published by"
          at={article.indexedAt}
          by={article.indexedBy}
        />
      )}
    </ButtonList>
  );
};

type PublishedProps = {
  unpublish: () => void;
  unpublishTranslation: () => void;
  article: ArticleData;
};

const Published = ({
  unpublish,
  unpublishTranslation,
  article,
}: PublishedProps) => {
  return (
    <ButtonList>
      <ButtonList.Item icon="cloud-download" onClick={unpublishTranslation}>
        Unpublish
      </ButtonList.Item>
      <ButtonList.Item icon="cloud-download" onClick={unpublish}>
        Unpublish All
      </ButtonList.Item>
      {article.indexedAt && article.indexedBy && (
        <>
          <ButtonList.Divider />
          <TimeAgo
            labelPrefix="Published by"
            at={article.indexedAt}
            by={article.indexedBy}
          />
        </>
      )}
    </ButtonList>
  );
};
